import { useEffect, useRef } from 'react';

const DEFAULT_SITE_URL = 'https://mulebuyspreadsheets.buzz';
export const SITE_URL = typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL
  ? import.meta.env.VITE_SITE_URL
  : DEFAULT_SITE_URL;

export const SITE_NAME = 'Mulebuy Spreadsheet Hub';

function upsertMetaTag(attr: string, attrValue: string, content: string) {
  const selector = attr === 'name'
    ? `meta[name="${attrValue}"]`
    : `meta[property="${attrValue}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = 'website',
  ogImage,
  jsonLd,
  noindex = false,
}: SEOHeadProps) {
  const cleanupRef = useRef<() => void>(() => {});

  useEffect(() => {
    document.title = title;

    const canonicalUrl = `${SITE_URL}${canonicalPath}`;

    upsertMetaTag('name', 'description', description);
    upsertMetaTag('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');
    upsertMetaTag('property', 'og:title', title);
    upsertMetaTag('property', 'og:description', description);
    upsertMetaTag('property', 'og:url', canonicalUrl);
    upsertMetaTag('property', 'og:type', ogType);
    upsertMetaTag('name', 'twitter:card', 'summary_large_image');
    upsertMetaTag('name', 'twitter:title', title);
    upsertMetaTag('name', 'twitter:description', description);

    if (keywords) {
      upsertMetaTag('name', 'keywords', keywords);
    }

    if (ogImage) {
      upsertMetaTag('property', 'og:image', ogImage);
    }

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonicalUrl);

    document.querySelectorAll('script[type="application/ld+json"][data-seo-jsonld]').forEach((el) => el.remove());

    if (jsonLd) {
      const dataItems = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      dataItems.forEach((item) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-seo-jsonld', 'true');
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
      });
    }

    cleanupRef.current = () => {
      document.querySelectorAll('script[type="application/ld+json"][data-seo-jsonld]').forEach((el) => el.remove());
    };

    return () => {
      if (cleanupRef.current) cleanupRef.current();
    };
  }, [title, description, keywords, canonicalPath, ogType, ogImage, JSON.stringify(jsonLd), noindex]);

  return null;
}